import { inject, computed, type ComputedRef, type Ref } from 'vue'
import type { AxiosInstance } from 'axios'

import type {
  MetadataFilterRequest,
  MetadataSearchParams,
  MetadataSearchResponse,
} from '@lib/types/core/MetadataFilter'
import type { StyledAutoCompleteOption } from '@lib/types/search'

export const SEARCH_TERM_PREFIX = '__search_term__'

interface MetadataUniqueValuesResponse {
  popular: Array<StyledAutoCompleteOption & { usage_count: number }>
  all: StyledAutoCompleteOption[]
}

interface UseMetadataSearchOptions {
  includeSearchTermAsOption?: boolean
}

export default function useMetadataSearch(options: UseMetadataSearchOptions = {}) {
  const metadataSearch = inject<{ (params: MetadataSearchParams): Promise<MetadataSearchResponse> }>('metadataSearch')
  const pitcherInfo = inject<ComputedRef<{ instanceId: string }>>(
    'pitcherInfo',
    computed(() => ({ instanceId: '' })),
  )
  const coreRestApiAxios = inject<Ref<AxiosInstance | undefined>>('coreRestApiAxios')

  function getDefaultAutoCompleteOption(searchTerm: string) {
    return {
      label: searchTerm,
      value: `${SEARCH_TERM_PREFIX}${searchTerm}`, // Unique value to prevent duplicate highlighting
      style: 'font-weight: bold;',
      isTypedSearchQuery: true,
    }
  }

  async function handleFetch(
    searchTerm: string,
    entity: MetadataFilterRequest['entity'],
    {
      includeNameSearch = false,
      includeDraftValues = false,
      isSection = false,
      isTemplate = false,
      isBlock = false,
      key = 'search_data',
      ownerId = null,
      searchMode = 'partial',
      threshold = undefined, // only used for fuzzy search
      fetchAllUnique = false,
      contentType = undefined,
      tagsNotIn = undefined,
    }: {
      includeNameSearch?: boolean
      includeDraftValues?: boolean
      isSection?: boolean
      isTemplate?: boolean
      isBlock?: boolean
      key?: MetadataSearchParams['key']
      ownerId?: number | null
      searchMode?: MetadataSearchParams['searchMode']
      threshold?: number
      fetchAllUnique?: boolean
      contentType?: string[]
      tagsNotIn?: string[]
    } = {},
  ): Promise<StyledAutoCompleteOption[]> {
    if (!metadataSearch || !searchTerm) {
      return []
    }

    try {
      const result = await metadataSearch({
        is_section: isSection,
        is_template: isTemplate,
        is_block: isBlock,
        include_name_search: includeNameSearch,
        include_draft_values: includeDraftValues,
        entity,
        values: [searchTerm],
        key,
        instance_id: pitcherInfo.value.instanceId,
        owner_id: ownerId?.toString(),
        searchMode,
        threshold,
        fetch_all_unique: fetchAllUnique,
        content_type: contentType,
        tags_not_in: tagsNotIn?.length ? tagsNotIn.join(',') : undefined,
      })

      const autocompleteOptions: StyledAutoCompleteOption[] = []
      const addedValues = new Set<string>() // Track added values to prevent duplicates

      if (options.includeSearchTermAsOption) {
        autocompleteOptions.push(getDefaultAutoCompleteOption(searchTerm))
        addedValues.add(searchTerm.toLowerCase())
      }

      if (result?.results && typeof result.results === 'object') {
        for (const [key, items] of Object.entries(result.results)) {
          // Check if this is an exact match with the search term
          const isExactMatch = key.toLowerCase() === searchTerm.toLowerCase()

          // Skip if we already have this value (but don't skip exact matches when we have search term option)
          if (addedValues.has(key.toLowerCase())) {
            // For exact matches, we still want to add them but not as the search term option
            if (!isExactMatch || !options.includeSearchTermAsOption) {
              continue
            }
          }

          if (Array.isArray(items)) {
            // Handle array case (MetadataResultItem[])
            if (items.length > 0 && 'id' in items[0]) {
              // For exact matches, mark as not a typed search query to distinguish from the search term option
              const optionToAdd: StyledAutoCompleteOption = {
                value: key,
                label: key,
                isTypedSearchQuery: false,
              }

              autocompleteOptions.push(optionToAdd)
              addedValues.add(key.toLowerCase())
            }
          } else if (
            items &&
            typeof items === 'object' &&
            'ids' in items &&
            Array.isArray(items.ids) &&
            items.ids.length > 0
          ) {
            // Handle MetadataResultFuzzy case
            // For exact matches, mark as not a typed search query to distinguish from the search term option
            const optionToAdd: StyledAutoCompleteOption = {
              value: key,
              label: key,
              isTypedSearchQuery: false,
            }

            autocompleteOptions.push(optionToAdd)
            addedValues.add(key.toLowerCase())
          }
        }
      }

      return autocompleteOptions
    } catch (error) {
      return options.includeSearchTermAsOption ? [getDefaultAutoCompleteOption(searchTerm)] : []
    }
  }

  async function fetchUniqueValues(
    entity: MetadataFilterRequest['entity'],
    {
      includeDraftValues = false,
      isSection = false,
      isTemplate = false,
      isBlock = false,
      key = 'search_data',
      ownerId = null,
      limit = 100,
      and = [],
      tagsNotIn = undefined,
    }: {
      includeDraftValues?: boolean
      isSection?: boolean
      isTemplate?: boolean
      isBlock?: boolean
      key?: MetadataSearchParams['key']
      ownerId?: number | null
      limit?: number
      and?: Array<{ key: string; value: string }>
      tagsNotIn?: string[]
    } = {},
  ): Promise<MetadataUniqueValuesResponse> {
    if (!entity) {
      return { popular: [], all: [] }
    }

    if (!coreRestApiAxios?.value) {
      console.error('Core REST API axios instance not available')
      return { popular: [], all: [] }
    }

    try {
      // Use the new metadata-suggestions endpoint with proper authentication
      const response = await coreRestApiAxios.value.post<
        unknown,
        {
          popular: Array<{ value: string; usage_count: number }>
          results: string[]
          pagination: {
            cursor: string | null
            has_more: boolean
          }
        }
      >('protected/metadata-suggestions', {
        key,
        entity,
        instance_id: pitcherInfo.value.instanceId,
        is_section: isSection,
        is_template: isTemplate,
        is_block: isBlock,
        include_draft_values: includeDraftValues,
        owner_id: ownerId?.toString(),
        page_size: limit,
        and,
        tags__not_in: tagsNotIn?.length ? tagsNotIn.join(',') : undefined,
      })

      if (response?.results && Array.isArray(response.results)) {
        return {
          popular:
            response.popular?.map((item) => ({
              value: item.value,
              label: item.value,
              usage_count: item.usage_count,
            })) || [],
          all: response.results.map((value: string) => ({
            value,
            label: value,
          })),
        }
      }

      return { popular: [], all: [] }
    } catch (error) {
      console.error('Failed to fetch unique values:', error)
      return { popular: [], all: [] }
    }
  }

  return {
    handleFetch,
    fetchUniqueValues,
  }
}
